import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { fmtMoney, fmtNum, fmtDate } from "../lib/format";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";
import ContractForm from "./ContractForm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function LinkDialog({ open, onClose }) {
  const qc = useQueryClient();
  const { data: pcs = [] } = useQuery({ queryKey: ["purchase-contracts"], queryFn: () => api.get("/purchase-contracts") });
  const { data: scs = [] } = useQuery({ queryKey: ["sale-contracts"], queryFn: () => api.get("/sale-contracts") });
  const [pc, setPc] = useState("");
  const [sc, setSc] = useState("");
  const [qty, setQty] = useState("");

  const save = async () => {
    if (!pc || !sc) return toast.error("Select both contracts");
    const p = pcs.find((x) => x.id === pc);
    await api.post("/contract-links", {
      purchase_contract_id: pc, sale_contract_id: sc,
      allocated_qty: Number(qty) || p?.quantity, coverage_pct: 100, note: "Manual back-to-back link",
    });
    toast.success("Back-to-back link created");
    qc.invalidateQueries();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="link-dialog">
        <DialogHeader><DialogTitle>Back-to-Back Contract Linking</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Purchase Contract</Label>
            <Select value={pc} onValueChange={setPc}>
              <SelectTrigger data-testid="link-pc"><SelectValue placeholder="Select purchase contract" /></SelectTrigger>
              <SelectContent>{pcs.filter(c => c.status !== "Cancelled").map((c) => <SelectItem key={c.id} value={c.id}>{c.contract_ref} · {c.commodity} · {fmtNum(c.quantity)}MT</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sale Contract</Label>
            <Select value={sc} onValueChange={setSc}>
              <SelectTrigger data-testid="link-sc"><SelectValue placeholder="Select sale contract" /></SelectTrigger>
              <SelectContent>{scs.filter(c => c.status !== "Cancelled").map((c) => <SelectItem key={c.id} value={c.id}>{c.contract_ref} · {c.commodity} · {fmtNum(c.quantity)}MT</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Allocated Quantity (MT)</Label>
            <Input data-testid="link-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Defaults to purchase qty" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="link-save" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={save}>Create Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractTable({ side }) {
  const qc = useQueryClient();
  const path = side === "purchase" ? "purchase-contracts" : "sale-contracts";
  const { data: rows = [] } = useQuery({ queryKey: [path], queryFn: () => api.get(`/${path}`) });
  const { data: links = [] } = useQuery({ queryKey: ["contract-links"], queryFn: () => api.get("/contract-links") });
  const [formOpen, setFormOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const coverage = (id) => {
    const rel = side === "purchase"
      ? links.filter((l) => l.purchase_contract_id === id)
      : links.filter((l) => l.sale_contract_id === id);
    return rel.length ? rel[0].coverage_pct : 0;
  };

  const confirm = async (r) => {
    await api.post(`/${path}/${r.id}/confirm`);
    toast.success(`${r.contract_ref} confirmed — journal entry posted`);
    qc.invalidateQueries();
  };

  const cols = [
    { key: "contract_ref", label: "Contract Ref", render: (r) => <span className="font-mono text-xs font-semibold text-orange-600 dark:text-orange-400">{r.contract_ref}</span> },
    { key: "commodity", label: "Commodity", render: (r) => <span className="font-semibold uppercase text-xs">{r.commodity}</span> },
    { key: "counterparty_name", label: side === "purchase" ? "Supplier" : "Buyer" },
    { key: "quantity", label: "Qty (MT)", align: "right", render: (r) => fmtNum(r.quantity) },
    { key: "price", label: "Price", align: "right", render: (r) => fmtMoney(r.price) },
    { key: "value", label: "Value", align: "right", render: (r) => fmtMoney(r.quantity * r.price) },
    { key: "delivery_term", label: "Terms", render: (r) => <span className="text-xs">{r.delivery_term} · {r.delivery_month}</span> },
    { key: "coverage", label: "B2B Cover", align: "right", render: (r) => <span className={coverage(r.id) >= 100 ? "text-emerald-600 font-semibold" : "text-amber-600"}>{coverage(r.id)}%</span> },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "_a", label: "Actions", render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {r.status === "Draft" && <button data-testid={`confirm-${r.contract_ref}`} onClick={() => confirm(r)} className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-950 text-emerald-600" title="Confirm"><CheckCircle2 className="h-4 w-4" /></button>}
      </div>
    ) },
  ];

  const expand = (r) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p className="text-xs text-slate-400">Origin</p><p>{r.origin}</p></div>
      <div><p className="text-xs text-slate-400">Load / Discharge</p><p>{r.load_port} → {r.discharge_port}</p></div>
      <div><p className="text-xs text-slate-400">Pricing Basis</p><p>{r.pricing_basis} {r.futures_ref !== "-" ? `(${r.futures_ref})` : ""}</p></div>
      <div><p className="text-xs text-slate-400">Payment</p><p>{r.payment_term}</p></div>
      <div><p className="text-xs text-slate-400">Tolerance</p><p>±{r.tolerance_pct}%</p></div>
      <div><p className="text-xs text-slate-400">Incoterm</p><p>{r.incoterm}</p></div>
      <div><p className="text-xs text-slate-400">Quality</p><p className="text-xs">{r.quality_terms}</p></div>
      <div><p className="text-xs text-slate-400">Version</p><p>v{r.version}</p></div>
    </div>
  );

  return (
    <>
      <DataTable
        testid={`${side}-contract-table`}
        title={side === "purchase" ? "Purchase Contracts (Buy Side)" : "Sale Contracts (Sell Side)"}
        columns={cols} rows={rows} csvName={`${side}_contracts`}
        onAdd={() => { setEdit(null); setFormOpen(true); }} addLabel={`New ${side === "purchase" ? "Purchase" : "Sale"}`}
        expandable={expand}
      />
      <ContractForm side={side} open={formOpen} onClose={() => setFormOpen(false)} initial={edit} />
    </>
  );
}

export default function Contracts() {
  const [linkOpen, setLinkOpen] = useState(false);
  return (
    <div data-testid="contracts-page">
      <Tabs defaultValue="purchase">
        <div className="flex items-center justify-between mb-4">
          <TabsList data-testid="contract-tabs">
            <TabsTrigger value="purchase" data-testid="tab-purchase">Purchase (Buy)</TabsTrigger>
            <TabsTrigger value="sale" data-testid="tab-sale">Sale (Sell)</TabsTrigger>
          </TabsList>
          <Button data-testid="open-link-dialog" variant="outline" onClick={() => setLinkOpen(true)}>
            <Link2 className="h-4 w-4 mr-2" /> Link Back-to-Back
          </Button>
        </div>
        <TabsContent value="purchase"><ContractTable side="purchase" /></TabsContent>
        <TabsContent value="sale"><ContractTable side="sale" /></TabsContent>
      </Tabs>
      <LinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} />
    </div>
  );
}
